/**
 * Project Card Component
 * Card view for a project in the grid
 * Aviation Dashboard Style
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Image, AlertTriangle, MoreVertical, Trash2, Edit, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { type DBProject } from '../../db';
import { projectsApi } from '../../lib/api';
import { ProjectMapThumbnail } from './ProjectMapThumbnail';

interface ProjectCardProps {
  project: DBProject & { boundingBox?: { north: number; south: number; east: number; west: number } };
  onDelete?: (projectId: string) => void;
}

// Aviation status badge
function AviationStatusBadge({ status }: { status: string }) {
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'EXPORTED':
        return 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-200 dark:border-green-500/30';
      case 'IN_PROGRESS':
        return 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/30';
      case 'PENDING':
        return 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30';
      default:
        return 'bg-slate-100 dark:bg-slate-500/20 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-500/30';
    }
  };

  return (
    <span className={`px-2 py-0.5 text-[10px] font-mono font-medium uppercase tracking-wider rounded border ${getStatusStyle(status)}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

export function ProjectCard({ project, onDelete }: ProjectCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!confirm(`Delete project "${project.code}"? This will mark it for deletion by an administrator.`)) {
      return;
    }

    setIsDeleting(true);
    try {
      await projectsApi.delete(project.id);
      toast.success('Project marked for deletion');
      // Dispatch custom event to notify all components
      window.dispatchEvent(new CustomEvent('projects-updated'));
      onDelete?.(project.id);
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project', {
        description: 'Please try again',
      });
    } finally {
      setIsDeleting(false);
      setMenuOpen(false);
    }
  };

  return (
    <div className="relative group">
      <Link
        to={`/projects/${project.id}`}
        className="block project-card p-4"
      >
        {/* Map Thumbnail */}
        <ProjectMapThumbnail
          boundingBox={project.boundingBox}
          className="aspect-video mb-3 relative"
        />

        {/* Project info */}
        <div>
          <h3 className="font-mono font-semibold text-slate-800 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors text-sm tracking-wide">
            {project.code}
          </h3>
          <p className="text-xs font-mono text-slate-500 truncate mt-0.5">{project.name}</p>
          {project.line && (
            <p className="text-xs font-mono text-slate-400 dark:text-slate-600 truncate">{project.line}</p>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-4 mt-3 text-xs font-mono text-slate-500 dark:text-slate-400">
          <div className="flex items-center gap-1" title="Photos">
            <Image className="w-3.5 h-3.5" />
            <span>{project.photoCount}</span>
          </div>
          <div className="flex items-center gap-1" title="Anomalies">
            <AlertTriangle className="w-3.5 h-3.5" />
            <span>{project.anomalyCount}</span>
          </div>
        </div>

        {/* Progress and status */}
        <div className="mt-3 space-y-2">
          <div className="flex items-center justify-between">
            <AviationStatusBadge status={project.status} />
            <span className="text-xs font-mono text-slate-500 tabular-nums">{project.progress}%</span>
          </div>
          <div className="progress-bar-aviation h-1">
            <div 
              className="progress-fill" 
              style={{ width: `${project.progress}%` }}
            />
          </div>
        </div>

        {/* Date */}
        <p className="text-[10px] font-mono text-slate-400 dark:text-slate-600 mt-3 tabular-nums">
          UPD :: {format(new Date(project.updatedAt), "yyyy.MM.dd HH:mm")}
        </p>

        {/* Hover indicator */}
        <div className="mt-2 flex items-center justify-end opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-xs font-mono text-blue-600 dark:text-blue-400 flex items-center gap-1">
            Open <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </Link>

      {/* Menu button */}
      <div className="absolute top-2 right-2">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMenuOpen(!menuOpen);
          }}
          className="p-1.5 bg-white/80 dark:bg-slate-800/80 backdrop-blur rounded-lg opacity-0 group-hover:opacity-100 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all shadow-sm"
        >
          <MoreVertical className="w-4 h-4 text-slate-500 dark:text-slate-400" />
        </button>

        {menuOpen && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMenuOpen(false);
              }} 
            />
            <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-slate-800/95 backdrop-blur rounded-lg shadow-lg border border-slate-200 dark:border-slate-700/50 z-50 py-1">
              <Link
                to={`/projects/${project.id}`}
                className="flex items-center gap-2 px-3 py-2 text-sm font-mono text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50"
                onClick={() => setMenuOpen(false)}
              >
                <Edit className="w-4 h-4" />
                Open
              </Link>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm font-mono text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
